import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { siteInfo } from "@/mocks/siteInfo";
import { categories } from "@/mocks/categories";
import { getArticleBySlug, getRelatedArticles, getRecentArticles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article, 3);
  const pageTitle = `${article.title} - ${siteInfo.name}`;
  const pageUrl = `${siteInfo.url}/article/${slug}`;
  const pageDesc = article.excerpt;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, item: { "@id": siteInfo.url, name: "首页" } },
      { "@type": "ListItem", position: 2, item: { "@id": `${siteInfo.url}/category/${article.category}`, name: category?.name || article.category } },
      { "@type": "ListItem", position: 3, item: { "@id": pageUrl, name: article.title } },
    ],
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [articleSchema, breadcrumbSchema],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${article.tags.join(",")},国模白露,白露写真,${siteInfo.keywords}`}
        canonicalUrl={pageUrl}
        ogImage={article.image}
        ogType="article"
        schemaData={combinedSchema}
      />

      <article className="pt-20 pb-12 md:pb-16 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: category?.name || article.category, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mt-6 mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
              <span>作者：{article.author}</span>
              <span>{article.date}</span>
              <span>阅读 {article.readTime}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover rounded-lg"
            />
          </div>

          <div className="prose prose-lg max-w-none text-foreground-700 leading-relaxed">
            <h2 className="text-xl font-semibold text-foreground-950 mb-4">
              作品赏析
            </h2>
            <p className="mb-4">
              本组写真作品由知名摄影师{article.author}掌镜，白露以极具张力的表现力与镜头感，展现出令人过目难忘的视觉魅力。整组作品在构图、光影与色彩的处理上都达到了很高的水准。
            </p>
            <p className="mb-4">
              在拍摄过程中，白露与摄影师团队密切配合，从妆造设计到场景选择，每一处细节都经过精心打磨。摄影师巧妙地运用了{article.category === "shinei-xiezhen" ? "室内" : "户外"}光线的变化，营造出层次丰富的画面效果。
            </p>
            <h2 className="text-xl font-semibold text-foreground-950 mb-4 mt-8">
              风格解析
            </h2>
            <p className="mb-4">
              这组作品展现了白露在{category?.name || ""}风格上的独特理解与诠释。她善于通过微妙的表情变化与肢体语言，将{category?.name || ""}主题的意境表达得淋漓尽致。
            </p>
            <p className="mb-4">
              从专业摄影角度来看，本组作品值得关注的技术亮点包括：精准的曝光控制使得高光与阴影过渡自然；巧妙的景深运用突出了主体与背景的关系；色彩调性统一而富有层次感。
            </p>
            <h2 className="text-xl font-semibold text-foreground-950 mb-4 mt-8">
              拍摄技巧参考
            </h2>
            <p className="mb-4">
              想要拍出类似效果的作品，建议从以下几个方面入手：首先，选择合适的拍摄时间和光线条件，{article.category === "huwai-renshen" ? "黄金时段（日出后和日落前）的柔和光线最为理想" : "利用窗边自然光或单灯侧光可以营造出立体感"}; 其次，注意背景与主体的色彩搭配和虚实关系; 最后，与模特的沟通至关重要，引导其进入自然的情绪状态。
            </p>
            <p className="mb-4">
              后期处理方面，建议保持自然的肤色质感，避免过度磨皮。通过微调色温、对比度和饱和度来强化整体氛围，让画面更加耐看。
            </p>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest Articles */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">
            最新内容
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {getRecentArticles(4).map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}